import { useState } from 'react';
import {
  Dialog, Button, TextField, Stack, Typography, Box, IconButton, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { api } from '../hooks/useApi';

interface Props {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    background: 'rgba(255,255,255,0.35)',
    backdropFilter: 'blur(8px)',
    border: 'none',
    '& fieldset': { border: '1px solid rgba(255,255,255,0.5)' },
    '&:hover fieldset': { border: '1px solid rgba(124,107,196,0.3)' },
    '&.Mui-focused fieldset': { border: '1.5px solid rgba(124,107,196,0.5)' },
  },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

export default function AddPatientDialog({ open, onClose, onAdded }: Props) {
  const [form, setForm] = useState({ first_name: '', last_name: '', date_of_birth: '', notes: '' });
  const theme = useTheme();

  const handleAdd = async () => {
    if (!form.first_name || !form.last_name) return;
    await api.createPatient(form);
    setForm({ first_name: '', last_name: '', date_of_birth: '', notes: '' });
    onClose();
    onAdded();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          background: 'linear-gradient(160deg, rgba(255,255,255,0.7) 0%, rgba(216,200,240,0.5) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.6)',
          boxShadow: '0 12px 48px rgba(124,107,196,0.15), 0 2px 12px rgba(0,0,0,0.06)',
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Typography variant="h6" fontWeight={700}>New Patient</Typography>
          <IconButton
            onClick={onClose} size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.4)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>

        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="First Name" required fullWidth size="small"
              value={form.first_name}
              onChange={e => setForm({ ...form, first_name: e.target.value })}
              sx={fieldSx}
            />
            <TextField
              label="Last Name" required fullWidth size="small"
              value={form.last_name}
              onChange={e => setForm({ ...form, last_name: e.target.value })}
              sx={fieldSx}
            />
          </Stack>
          <TextField
            label="Notes" fullWidth multiline rows={2} size="small"
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            sx={fieldSx}
          />
        </Stack>

        <Stack direction="row" spacing={1.5} mt={3}>
          <Button
            fullWidth onClick={onClose}
            sx={{
              borderRadius: '14px', py: 1.1, fontWeight: 600,
              bgcolor: 'rgba(255,255,255,0.4)',
              color: theme.palette.text.primary,
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.5)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth onClick={handleAdd}
            disabled={!form.first_name || !form.last_name}
            sx={{
              borderRadius: '14px', py: 1.1, fontWeight: 600,
              background: 'linear-gradient(135deg, #c4b0e8 0%, #7c6bc4 100%)',
              color: '#fff',
              boxShadow: '0 4px 16px rgba(124,107,196,0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #b8a0e0 0%, #6a5ab0 100%)',
                boxShadow: '0 6px 24px rgba(124,107,196,0.45)',
              },
              '&.Mui-disabled': {
                background: 'rgba(0,0,0,0.08)',
                color: 'rgba(0,0,0,0.26)',
                boxShadow: 'none',
              },
            }}
          >
            Add Patient
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}
